import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import type { ApiKeyFormValues } from '../schemas/api-key-form-schema';

import { ScopePicker } from './scope-picker';

export interface ApiKeyFieldsProps {
  form: UseFormReturn<ApiKeyFormValues>;
  /** Editing: a key keeps its kind for life. */
  kindLocked?: boolean;
}

/** Name, kind, origins, scopes and rate limit, shared by the create and edit dialogs. */
export function ApiKeyFields({ form, kindLocked = false }: ApiKeyFieldsProps) {
  const { t } = useTranslation(['apiKeys', 'common']);
  const kind = useWatch({ control: form.control, name: 'kind' });
  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('apiKeys:fields.name')}</FormLabel>
            <FormControl>
              <Input
                autoComplete="off"
                placeholder="Next.js storefront"
                autoFocus={!kindLocked}
                {...field}
              />
            </FormControl>
            <FormDescription>{t('apiKeys:fields.nameHint')}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {kindLocked ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t('apiKeys:fields.kind')}</span>
          <span className="text-sm">{t(`apiKeys:kind.${kind}`)}</span>
          <span className="text-muted-foreground text-xs">{t('apiKeys:edit.kindLocked')}</span>
        </div>
      ) : (
        <FormField
          control={form.control}
          name="kind"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('apiKeys:fields.kind')}</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid gap-2 sm:grid-cols-2"
                >
                  {(['publishable', 'secret'] as const).map((option) => (
                    <FormItem
                      key={option}
                      className="flex flex-row items-start gap-2 rounded-md border p-3"
                    >
                      <FormControl>
                        <RadioGroupItem value={option} className="mt-0.5" />
                      </FormControl>
                      <div className="flex flex-col gap-0.5">
                        <FormLabel className="font-medium">{t(`apiKeys:kind.${option}`)}</FormLabel>
                        <span className="text-muted-foreground text-xs">
                          {t(`apiKeys:kindHint.${option}`)}
                        </span>
                      </div>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {kind === 'publishable' ? (
        <FormField
          control={form.control}
          name="allowedOrigins"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('apiKeys:fields.allowedOrigins')}</FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  className="font-mono text-[13px]"
                  placeholder={'https://shop.example.com\nhttp://localhost:3000'}
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('apiKeys:fields.allowedOriginsHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      <FormField
        control={form.control}
        name="scopes"
        render={({ field }) => (
          <FormItem>
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{t('apiKeys:fields.scopes')}</legend>
              <ScopePicker value={field.value} onChange={field.onChange} />
            </fieldset>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="rateLimitPerMinute"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('apiKeys:fields.rateLimit')}</FormLabel>
            <FormControl>
              <Input
                inputMode="numeric"
                autoComplete="off"
                className="max-w-40"
                placeholder={t('apiKeys:fields.rateLimitPlaceholder')}
                {...field}
              />
            </FormControl>
            <FormDescription>{t('apiKeys:fields.rateLimitHint')}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
